import { useState, useEffect } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  Card,
  Group,
  Text,
  Avatar,
  Badge,
  Box,
  Collapse,
  ActionIcon,
  Button,
  Divider,
  Table,
  Checkbox,
  Tooltip,
  Menu,
} from '@mantine/core';
import {
  Users,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  UserPlus,
  Calendar,
  MoreVertical,
  UserMinus,
  Plus,
  Eye,
  CheckCircle,
  XCircle,
  Trash2,
  Edit2,
} from 'react-feather';
import type { GroupWithMembers, GroupMember } from '../../../services/api/groups';
import type { Participant, OverallHealthStatus } from '../../../types';
import { WithdrawnPill } from './participants/ParticipantCellRenderers';
import { GroupNoTimeSlotEmptyState } from './GroupNoTimeSlotEmptyState';
import { MatchingToolPill } from './MatchingToolPill';
import { AttendanceRate, ParticipantAvatar } from '../../../components/common';
import classes from './GroupCard.module.css';
import { isMedleyEmail } from '../../../utils/medleyEmail';
import {
  NO_MEDLEY_EMAIL_BADGE_STYLES,
  NO_MEDLEY_EMAIL_LABEL,
} from '../../../components/coaches/noMedleyEmailBadge';

// The participant fields GroupCard reads. A full Participant satisfies it;
// the read-only coach card (MED-1367) builds it from the coach API.
export type GroupCardParticipant = Pick<
  Participant,
  'id' | 'status' | 'withdrawn_at' | 'has_open_flag' | 'flag_id' | 'personality_type' | 'custom_fields'
>;

// Session data structure (until API supports it)
interface GroupSession {
  id: string;
  number: number;
  topic: string;
  date: string;
  time: string;
  coachName: string | null;
  status: string;
  // MED-1367: set by the read-only coach card. A past session shows quieter.
  isPast?: boolean;
}

interface GroupCardProps {
  group: GroupWithMembers;
  cohortId: string;
  colorIndex?: number;
  // MED-1161: peer-led cohorts have no per-group coach — hide the coach badge,
  // the sessions table "COACH" column, and the "Assign Coach" footer button.
  isPeerLed?: boolean;
  sessions?: GroupSession[];
  // MED-977: configured group-coaching session count from the cohort's
  // modality. Used as the denominator when the group has no scheduled
  // sessions yet, replacing a hardcoded `|| 6` fallback.
  configuredSessionCount?: number | null;
  schedule?: string; // e.g., "Thursdays 2:00 - 3:30 PM PT"
  participantMap?: Record<string, GroupCardParticipant>; // Map of user_id -> Participant for demographics
  /**
   * MED-1294: open the rename modal for this group. Was previously declared as
   * `onEditGroup` and never destructured or passed by any caller — renamed to say
   * what it does now that something calls it.
   */
  onRenameGroup?: (group: GroupWithMembers) => void;
  onChangeCoach?: (group: GroupWithMembers) => void;
  onAddParticipant?: (group: GroupWithMembers) => void;
  onViewAllParticipants?: (group: GroupWithMembers) => void;
  onRemoveMember?: (group: GroupWithMembers, member: GroupMember) => void;
  onDeleteGroup?: (group: GroupWithMembers) => void;
  onManageSessions?: () => void;
  onAddTimeSlot?: (group: GroupWithMembers) => void; // For groups without a time slot
  onEditTimeSlot?: (group: GroupWithMembers) => void; // For groups with a time slot (MED-648)
  isDropTarget?: boolean;
  dragCount?: number;
  defaultExpanded?: boolean;
  // Selection mode props (for coach-to-groups assignment)
  isSelectionMode?: boolean;
  isSelected?: boolean;
  isAvailableForSelection?: boolean;
  onToggleSelection?: () => void;
  // MED-114: Health status for group
  healthStatus?: OverallHealthStatus;
  // MED-117: Flag indicator callbacks
  onViewParticipant?: (userId: string) => void;
  onResolveMemberFlag?: (participantId: string, flagId: string) => void;
  onDismissMemberFlag?: (participantId: string, flagId: string) => void;
  // MED-1367: view-only card for the coach portal. Hides every edit control
  // (rename, time slot, coach box, member menus, Manage sessions, footer) and
  // the intake demographics a coach does not receive. Disables drag and drop.
  readOnly?: boolean;
  // MED-1367: read-only coach card only. A click (or Enter or Space) on a
  // session row opens the coach session details drawer.
  onSessionClick?: (sessionId: string) => void;
}

// Vibrant group avatar colors (same palette as participants/coaches)
import { getGroupAvatarColor } from '../../../utils/groupColors';

// Format gender identity to display letter
const getGenderLetter = (gender?: string): string | null => {
  if (!gender) return null;
  const lower = gender.toLowerCase();
  if (lower.includes('woman') || lower.includes('female')) return 'F';
  if (lower.includes('man') || lower.includes('male')) return 'M';
  if (lower.includes('non-binary') || lower.includes('nonbinary')) return 'NB';
  return gender.charAt(0).toUpperCase();
};

export function GroupCard({
  group,
  colorIndex: _colorIndex = 0,
  isPeerLed = false,
  sessions = [],
  configuredSessionCount,
  schedule,
  participantMap = {},
  onRenameGroup,
  onChangeCoach,
  onAddParticipant,
  onDeleteGroup,
  onRemoveMember,
  onManageSessions,
  onAddTimeSlot,
  onEditTimeSlot,
  isDropTarget = true,
  dragCount = 1,
  defaultExpanded = true,
  isSelectionMode = false,
  isSelected = false,
  isAvailableForSelection = true,
  onToggleSelection,
  healthStatus: _healthStatus,
  onViewParticipant,
  onResolveMemberFlag,
  onDismissMemberFlag,
  readOnly = false,
  onSessionClick,
}: GroupCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Sync expanded state with parent's defaultExpanded prop
  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  // Droppable setup. The hook always runs (hooks rules); a read-only card
  // disables it and never attaches the drop ref.
  const { isOver, setNodeRef } = useDroppable({
    id: `group-${group.id}`,
    data: { group },
    disabled: readOnly,
  });

  const groupAvatarColor = getGroupAvatarColor(group.name, group.id);

  // Capacity calculations
  const currentCount = group.member_count;
  const maxSize = group.max_size || 6;
  const wouldExceedCapacity = currentCount + dragCount > maxSize;
  const isAtCapacity = currentCount >= maxSize;

  // Calculate stats
  const completedSessions = sessions.filter(s => s.status === 'completed').length;
  // MED-977: when no sessions exist yet, fall back to the cohort's configured
  // group-coaching session count — never a hardcoded magic number.
  const totalSessions = sessions.length || (configuredSessionCount ?? 0);

  // MED-1266: attendance is the canonical server-computed group rate — the frontend no
  // longer sums per-participant figures (AC1). Coverage travels with it.
  const attendancePercentage: number | null = group.attendance_percentage ?? null;

  // MED-289: Average engagement from API
  const avgEngagement: number | null = group.avg_engagement ?? null;

  // Calculate gender breakdown from participant intake data
  const genderCounts = {
    'NON-BINARY': 0,
    'WOMAN': 0,
    'MAN': 0,
  };

  // Calculate personality breakdown from cohort_participants.personality_type
  const personalityCounts = {
    extrovert: 0,
    introvert: 0,
  };

  // Count demographics from actual participant data
  group.members.forEach((member) => {
    const participant = participantMap[member.user_id];
    if (participant) {
      // Count personality type
      const personality = participant.personality_type?.toLowerCase() || '';
      if (personality.includes('extrovert') || personality === 'e') {
        personalityCounts.extrovert++;
      } else if (personality.includes('introvert') || personality === 'i') {
        personalityCounts.introvert++;
      }

      // Count gender from custom_fields (intake form response)
      const gender = participant.custom_fields?.gender || participant.custom_fields?.gender_identity || '';
      const genderLower = gender.toLowerCase();
      if (genderLower.includes('non-binary') || genderLower.includes('nonbinary')) {
        genderCounts['NON-BINARY']++;
      } else if (genderLower.includes('woman') || genderLower.includes('female') || genderLower === 'f') {
        genderCounts['WOMAN']++;
      } else if (genderLower.includes('man') || genderLower.includes('male') || genderLower === 'm') {
        genderCounts['MAN']++;
      }
    }
  });

  // Determine if this card is unavailable in selection mode
  const isUnavailable = isSelectionMode && !isAvailableForSelection;

  // Handle click on the card header - either toggle selection or expand/collapse
  const handleHeaderClick = () => {
    if (isSelectionMode && isAvailableForSelection && onToggleSelection) {
      onToggleSelection();
    } else if (!isSelectionMode) {
      setExpanded(!expanded);
    }
  };

  // Handle checkbox click (prevent propagation to avoid double-triggering)
  const handleCheckboxChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelection) {
      onToggleSelection();
    }
  };

  // Handle expand/collapse chevron click in selection mode
  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <Tooltip
      label="Coach is not available for this time slot"
      disabled={!isUnavailable}
      position="top"
    >
      <Card
        ref={isDropTarget && !readOnly ? setNodeRef : undefined}
        withBorder
        className={`${classes.card} ${isOver ? classes.dropOver : ''} ${isOver && wouldExceedCapacity ? classes.dropWarning : ''} ${isUnavailable ? classes.unavailable : ''} ${isSelected ? classes.selected : ''} ${readOnly ? classes.readOnly : ''}`}
      >
        {/* Drop indicator */}
        {isOver && (
          <Box className={classes.dropIndicator}>
            {wouldExceedCapacity ? (
              <Group gap="xs" justify="center">
                <AlertTriangle size={16} />
                <Text size="sm" fw={500}>
                  {isAtCapacity ? 'Group is at capacity' : `Would exceed capacity (${maxSize} max)`}
                </Text>
              </Group>
            ) : (
              <Text size="sm" fw={500}>Drop to assign to {group.name}</Text>
            )}
          </Box>
        )}

        {/* Header - Always Visible */}
        <Box
          className={`${classes.header} ${readOnly ? classes.readOnlyHeader : ''}`}
          onClick={handleHeaderClick}
          style={{ cursor: isUnavailable ? 'not-allowed' : 'pointer' }}
        >
          <Group justify="space-between" wrap="nowrap" className={classes.headerRow}>
            {/* Left side - Selection checkbox + Group info */}
            <Group gap="md" wrap="nowrap" className={classes.headerRow}>
              {/* Selection Checkbox - only in selection mode for available groups */}
              {isSelectionMode && isAvailableForSelection && (
                <Checkbox
                  checked={isSelected}
                  onChange={() => {}}
                  onClick={handleCheckboxChange}
                  size="md"
                  color="blue"
                  styles={{
                    input: { cursor: 'pointer' },
                  }}
                />
              )}

              {/* Group Avatar */}
              <Avatar
              size={40}
              radius="xl"
              styles={{
                root: { backgroundColor: groupAvatarColor },
                placeholder: { color: '#FFFFFF', backgroundColor: groupAvatarColor },
              }}
            >
              <Users size={20} color="white" />
            </Avatar>

            {/* Group Name & Participant Count */}
            <div>
              <Group gap={6} wrap="nowrap">
                <Text fw={600} size="sm">{group.name}</Text>
                {/* MED-1294 (AC1/AC2): rename entry for the list view. A separate
                    icon rather than a clickable name — the card header toggles
                    expand/collapse, so the name itself is already a hit target and
                    stopPropagation on it would silently break that. */}
                {!readOnly && onRenameGroup && (
                  <Tooltip label="Rename group">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={`Rename ${group.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRenameGroup(group);
                      }}
                    >
                      <Edit2 size={13} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
              <Text size="13px" c="dimmed">
                {group.member_count} participant{group.member_count !== 1 ? 's' : ''}
              </Text>
            </div>
            {/* Matching Tool Pill — shown only when the group was last written by the matching server (E23-11 / MED-926) */}
            {group.synced_by_matching_session_id && <MatchingToolPill />}
            {/* Time Slot Pill or Add Time Slot Button */}
            {schedule ? (
              <Tooltip label="Click to change time slot" disabled={readOnly || !onEditTimeSlot}>
                <Badge
                  variant="light"
                  color="gray"
                  size="lg"
                  radius="xl"
                  styles={{
                    root: !readOnly && onEditTimeSlot ? { cursor: 'pointer' } : undefined,
                    label: { fontWeight: 500, fontSize: 13, textTransform: 'none', letterSpacing: 0 },
                  }}
                  onClick={
                    !readOnly && onEditTimeSlot
                      ? (e) => {
                          e.stopPropagation();
                          onEditTimeSlot(group);
                        }
                      : undefined
                  }
                >
                  {schedule}
                </Badge>
              </Tooltip>
            ) : !readOnly && onAddTimeSlot && (
              <Button
                variant="light"
                size="xs"
                leftSection={<Plus size={12} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddTimeSlot(group);
                }}
                styles={{
                  root: {
                    fontWeight: 500,
                  },
                }}
              >
                Add Time Slot
              </Button>
            )}
          </Group>

          {/* Right side - Stats */}
          <Group gap="sm" wrap="nowrap" className={`${classes.headerRow} ${classes.statsRow}`}>
            {/* Sessions */}
            <Box ta="center" className={classes.statBox}>
              <Text fw={700} size="20px" lh={1}>{completedSessions}/{totalSessions}</Text>
              <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} mt={4}>Sessions</Text>
            </Box>

            {/* Attendance — MED-1266: canonical server rate + marking coverage */}
            <Box ta="center" className={classes.statBox}>
              <AttendanceRate
                rate={attendancePercentage}
                marked={group.attendance_marked}
                population={group.attendance_population}
                variant="stacked"
                size="20px"
                fw={700}
              />
              <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} mt={4}>Attendance</Text>
            </Box>

            {/* Avg Engagement */}
            <Box ta="center" className={classes.statBox}>
              <Text fw={700} size="20px" lh={1}>{avgEngagement !== null ? avgEngagement.toFixed(1) : '—'}</Text>
              <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} mt={4}>Avg Engagement</Text>
            </Box>

            {/* Coach — MED-1161: hidden for peer-led cohorts (no per-group coach). */}
            {/* MED-1367: hidden on the read-only coach card; the viewer is the coach. */}
            {!isPeerLed && !readOnly && (group.coach_id ? (
              <Group
                gap="xs"
                style={{
                  backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
                  borderRadius: 6,
                  padding: '14px 14px',
                }}
                wrap="nowrap"
              >
                <Avatar size="sm" radius="xl" color="medleyGreen">
                  {(group.coach_name || '')
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </Avatar>
                <div>
                  <Text size="9px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                    Coach
                  </Text>
                  <Text size="sm" fw={500}>
                    {group.coach_name || 'Assigned'}
                  </Text>
                  {/* MED-1355: a lead set before the rule can lack a Medley
                      email. The badge tells the CM to add one. A group read
                      without coach_email shows no badge. */}
                  {group.coach_email !== undefined && !isMedleyEmail(group.coach_email) && (
                    <Badge
                      styles={NO_MEDLEY_EMAIL_BADGE_STYLES}
                      mt={2}
                      title="Add a Medley email on the coach profile."
                    >
                      {NO_MEDLEY_EMAIL_LABEL}
                    </Badge>
                  )}
                </div>
              </Group>
            ) : (
              <Box
                className={classes.statBox}
                style={{
                  backgroundColor: '#F5EDD8',
                  borderRadius: 8,
                  padding: '8px 12px',
                }}
              >
                <Text size="xs" c="dimmed" tt="uppercase">Coach</Text>
                <Text size="sm" c="#8B7355" fs="italic">Not assigned</Text>
              </Box>
            ))}

            {/* Expand/Collapse */}
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              className={classes.collapseToggle}
              aria-label={expanded ? 'Collapse group' : 'Expand group'}
              aria-expanded={expanded}
              onClick={isSelectionMode ? handleExpandClick : undefined}
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </ActionIcon>
          </Group>
        </Group>
      </Box>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Divider />
        <Box className={`${classes.expandedContent} ${isSelectionMode ? classes.selectionModeDisabled : ''}`}>
          <Group align="flex-start" wrap="nowrap" gap={0} className={classes.bodyRow}>
            {/* Left Column - Participants */}
            <Box className={classes.columnParticipants}>
              {/* Participants Header */}
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <Users size={16} />
                  <Text fw={600} size="sm">Participants</Text>
                  <Badge size="xs" color="gray" variant="light">{group.member_count}</Badge>
                </Group>
                {/* MED-1367: the coach API carries no intake demographics, so the
                    read-only card would only show empty counts. */}
                {!readOnly && (
                  <Group gap={6}>
                    {genderCounts['NON-BINARY'] > 0 && (
                      <Badge size="xs" variant="light" color="gray">
                        {genderCounts['NON-BINARY']} NON-BINARY
                      </Badge>
                  )}
                  {genderCounts['WOMAN'] > 0 && (
                    <Badge size="xs" variant="light" color="gray">
                      {genderCounts['WOMAN']} WOMAN
                    </Badge>
                  )}
                  {genderCounts['MAN'] > 0 && (
                    <Badge size="xs" variant="light" color="gray">
                      {genderCounts['MAN']} MAN
                    </Badge>
                  )}
                  <Badge size="xs" variant="light" color="gray">
                    {personalityCounts.extrovert}/{personalityCounts.introvert} E/I
                  </Badge>
                </Group>
                )}
              </Group>

              {/* Participants Table */}
              {group.members.length > 0 ? (
                <Table className={classes.participantsTable} verticalSpacing="xs">
                  <Table.Tbody>
                    {group.members.map((member) => {
                      const participant = participantMap[member.user_id];
                      const genderValue = participant?.custom_fields?.gender || participant?.custom_fields?.gender_identity || '';
                      const personalityValue = participant?.personality_type || '';

                      // Format gender for display
                      const genderDisplay = getGenderLetter(genderValue) || '-';

                      // Format personality for display
                      let personalityDisplay = '-';
                      if (personalityValue) {
                        const lowerPersonality = personalityValue.toLowerCase();
                        if (lowerPersonality.includes('extrovert') || lowerPersonality === 'e') {
                          personalityDisplay = 'Extrovert';
                        } else if (lowerPersonality.includes('introvert') || lowerPersonality === 'i') {
                          personalityDisplay = 'Introvert';
                        } else {
                          personalityDisplay = personalityValue;
                        }
                      }

                      const isWithdrawn = participant?.status === 'withdrawn';
                      const isFlagged = participant?.has_open_flag ?? false;

                      return (
                        <Table.Tr
                          key={member.user_id}
                          className={classes.participantRow}
                          style={{
                            cursor: onViewParticipant ? 'pointer' : undefined,
                            ...(isFlagged && !isWithdrawn ? { backgroundColor: '#FEF3E7' } : {}),
                            // MED-1367: grey text, faded avatar; the row stays readable.
                            ...(isWithdrawn ? { color: '#666666' } : {}),
                          }}
                          onClick={() => onViewParticipant?.(member.user_id)}
                        >
                          <Table.Td>
                            <Group gap="sm" wrap="nowrap">
                              {/* MED-1367: same name-based colour as the participants table. */}
                              <ParticipantAvatar
                                firstName={member.first_name}
                                lastName={member.last_name}
                                size={32}
                                style={isWithdrawn ? { opacity: 0.5 } : undefined}
                              />
                              <Box>
                                <Group gap={6} wrap="nowrap">
                                  <Text size="sm" fw={500} style={{ whiteSpace: 'nowrap' }}>{member.first_name} {member.last_name}</Text>
                                  {isWithdrawn && (
                                    <WithdrawnPill withdrawnAt={participant?.withdrawn_at ?? null} />
                                  )}
                                  {!isWithdrawn && isFlagged && (
                                    <Badge size="xs" color="warningAmber" variant="light" leftSection={<AlertTriangle size={10} />} style={{ flexShrink: 0 }}>
                                      Flagged
                                    </Badge>
                                  )}
                                </Group>
                                <Text size="xs" c="dimmed">{member.email || '-'}</Text>
                              </Box>
                            </Group>
                          </Table.Td>
                          {!readOnly && (
                            <>
                              <Table.Td style={{ width: 30 }}>
                                <Text size="xs" c="dimmed">{genderDisplay}</Text>
                              </Table.Td>
                              <Table.Td style={{ width: 70 }}>
                                <Text size="xs" c="dimmed">{personalityDisplay}</Text>
                              </Table.Td>
                              <Table.Td style={{ width: 40 }}>
                                <Menu shadow="md" width={180} position="bottom-end">
                                  <Menu.Target>
                                    <ActionIcon
                                      variant="subtle"
                                      color="gray"
                                      size="sm"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <MoreVertical size={16} />
                                    </ActionIcon>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    <Menu.Item
                                      leftSection={<Eye size={14} />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onViewParticipant?.(member.user_id);
                                      }}
                                    >
                                      View Participant
                                    </Menu.Item>
                                    {isFlagged && participant?.flag_id && (
                                      <>
                                        <Menu.Item
                                          leftSection={<CheckCircle size={14} />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onResolveMemberFlag?.(participant.id, participant.flag_id!);
                                          }}
                                        >
                                          Mark as Resolved
                                        </Menu.Item>
                                        <Menu.Item
                                          leftSection={<XCircle size={14} />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onDismissMemberFlag?.(participant.id, participant.flag_id!);
                                          }}
                                        >
                                          Dismiss Flag
                                        </Menu.Item>
                                      </>
                                    )}
                                    <Menu.Divider />
                                    <Menu.Item
                                      leftSection={<UserMinus size={14} />}
                                      color="red"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveMember?.(group, member);
                                      }}
                                    >
                                      Remove from group
                                    </Menu.Item>
                                  </Menu.Dropdown>
                                </Menu>
                              </Table.Td>
                            </>
                          )}
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text size="sm" c="dimmed" fs="italic">
                  No participants assigned
                </Text>
              )}
            </Box>

            {/* Vertical Divider */}
            <Divider orientation="vertical" className={classes.verticalDivider} />

            {/* Right Column - Sessions */}
            <Box className={classes.columnSessions}>
              {/* Sessions Header */}
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <Calendar size={16} />
                  <Text fw={600} size="sm">Sessions</Text>
                  <Badge size="xs" color="gray" variant="light">{totalSessions}</Badge>
                </Group>
                {!readOnly && (
                  <Button
                    variant="default"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageSessions?.();
                    }}
                  >
                    Manage sessions
                  </Button>
                )}
              </Group>

              {/* Sessions Table */}
              {sessions.length > 0 ? (
                <Table className={classes.sessionsTable} verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }}>#</Table.Th>
                      <Table.Th>TOPIC</Table.Th>
                      <Table.Th>DATE</Table.Th>
                      {/* MED-1161: COACH column hidden for peer-led cohorts (AC5). */}
                      {!isPeerLed && <Table.Th>COACH</Table.Th>}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {sessions.map((session, index) => {
                      // MED-1367: past styling and row clicks apply to the
                      // read-only coach card only. The staff card is unchanged.
                      const isPastSession = readOnly && !!session.isPast;
                      const openSession =
                        readOnly && onSessionClick
                          ? () => onSessionClick(session.id)
                          : undefined;
                      return (
                        <Table.Tr
                          key={session.id}
                          className={
                            [
                              isPastSession ? classes.pastSession : '',
                              openSession ? classes.clickableSession : '',
                            ]
                              .filter(Boolean)
                              .join(' ') || undefined
                          }
                          onClick={
                            openSession
                              ? (e) => {
                                  e.stopPropagation();
                                  openSession();
                                }
                              : undefined
                          }
                          onKeyDown={
                            openSession
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openSession();
                                  }
                                }
                              : undefined
                          }
                          tabIndex={openSession ? 0 : undefined}
                          aria-label={
                            openSession
                              ? `Open session ${session.number || index + 1} details${isPastSession ? ' (past session)' : ''}`
                              : undefined
                          }
                        >
                          <Table.Td>
                            <span className={classes.sessionNumber}>
                              S{session.number || index + 1}
                            </span>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c={isPastSession ? 'dimmed' : undefined}>
                              {session.topic}
                            </Text>
                            {isPastSession && (
                              <Text span className={classes.pastLabel}>
                                Past
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              <span className={classes.nowrap}>{session.date}</span>
                              {/* MED-1367: date and time each stay on one line; the
                                  cell breaks only between them. */}
                              {session.time && (
                                <>
                                  {' '}
                                  <span className={classes.nowrap}>{session.time}</span>
                                </>
                              )}
                            </Text>
                          </Table.Td>
                          {/* MED-1161: COACH cell hidden for peer-led cohorts (AC5). */}
                          {!isPeerLed && (
                            <Table.Td>
                              <Text
                                size="sm"
                                c={session.coachName && !isPastSession ? undefined : 'dimmed'}
                              >
                                {session.coachName || 'No coach assigned'}
                              </Text>
                            </Table.Td>
                          )}
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              ) : !readOnly && !schedule && onAddTimeSlot ? (
                <GroupNoTimeSlotEmptyState onAddTimeSlot={() => onAddTimeSlot(group)} />
              ) : (
                <Text size="sm" c="dimmed" fs="italic" ta="center" py="md">
                  No sessions scheduled for this group
                </Text>
              )}
            </Box>
          </Group>
        </Box>

        {/* Footer - MED-1367: hidden on the read-only coach card. */}
        {!readOnly && (
          <Box className={`${classes.footer} ${isSelectionMode ? classes.selectionModeDisabled : ''}`}>
            <Group justify="space-between">
              <Group gap="sm">
                <Button
                  color="red"
                  variant="light"
                  size="sm"
                  leftSection={<Trash2 size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteGroup?.(group);
                  }}
                >
                  Delete Group
                </Button>
                {/* MED-1161: Assign Coach hidden for peer-led cohorts (AC4). */}
                {!isPeerLed && (
                  <Button
                    variant="default"
                    size="sm"
                    leftSection={<Users size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChangeCoach?.(group);
                    }}
                  >
                    Assign Coach
                  </Button>
              )}
            </Group>
            <Button
              size="sm"
              leftSection={<UserPlus size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onAddParticipant?.(group);
              }}
            >
              Add Participant
            </Button>
          </Group>
        </Box>
        )}
      </Collapse>
      </Card>
    </Tooltip>
  );
}

export default GroupCard;
